/**
 * Compares two sort values with empty values last.
 * Numbers compare numerically; everything else compares as text, with digit
 * sequences ordered numerically and case ignored.
 *
 * @param {*} aValue - Left sort value.
 * @param {*} bValue - Right sort value.
 * @param {"asc"|"desc"} direction - Sort direction.
 * @returns {number} Compare result for Array.sort.
 */
export function compareSortValues(aValue, bValue, direction) {
  const aEmpty = aValue === null || aValue === undefined || aValue === "";
  const bEmpty = bValue === null || bValue === undefined || bValue === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let result = 0;
  if (typeof aValue === "number" && typeof bValue === "number") {
    result = aValue - bValue;
  } else {
    result = String(aValue).localeCompare(String(bValue), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  return direction === "desc" ? -result : result;
}

/**
 * Copy a list and sort it alphabetically by a text field.
 *
 * @template T
 * @param {Array<T>|null|undefined} items Rows to sort. A missing list is treated as empty.
 * @param {string} key Property whose string value sets the order.
 * @returns {Array<T>}
 */
export function sortAlphabetically(items, key) {
  const list = Array.isArray(items) ? [...items] : [];
  return list.sort((a, b) =>
    String(a?.[key] || "").localeCompare(String(b?.[key] || ""))
  );
}

/**
 * Copy a list, replace the row with the same id, and sort alphabetically.
 *
 * @template T
 * @param {Array<T>|null|undefined} items Current rows.
 * @param {T} item Row that includes `id`.
 * @param {string} key Property whose string value sets the order.
 * @returns {Array<T>}
 */
export function upsertAlphabetically(items, item, key) {
  const list = Array.isArray(items) ? items : [];
  const withoutMatch = list.filter(
    (row) => String(row?.id) !== String(item?.id)
  );
  return sortAlphabetically([...withoutMatch, item], key);
}
